import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';

import styles from './OrgIconEditor.module.css';

interface OrgIconEditorProps {
  orgName: string;
  /** Existing icon (data URL) to seed the cropper with, if any. */
  currentIcon: string | null;
  /** Save the cropped icon (data URL), or null to remove it. */
  onSave: (icon: string | null) => void;
  onClose: () => void;
  saving?: boolean;
  error?: string | null;
}

// Output edge length. 256px is crisp on retina for the small (28-36px)
// places the icon renders, and stays tiny on the wire (~10-30KB).
const OUTPUT_SIZE = 256;

/**
 * Modal to upload and crop an org icon. The user picks a file (or re-crops
 * the existing icon), pans/zooms a square selection, and saves. The crop
 * is rasterized client-side to a small data URL so the API stays JSON.
 */
export function OrgIconEditor({
  orgName,
  currentIcon,
  onSave,
  onClose,
  saving = false,
  error = null,
}: OrgIconEditorProps) {
  const [src, setSrc] = useState<string | null>(currentIcon);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSrc(typeof reader.result === 'string' ? reader.result : null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const onConfirm = async () => {
    if (!src || !areaPixels) return;
    const cropped = await cropToDataUrl(src, areaPixels, OUTPUT_SIZE);
    onSave(cropped);
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit icon for ${orgName}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className={styles.title}>Org icon</h2>

        {src ? (
          <div className={styles.cropArea}>
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        ) : (
          <button type="button" className={styles.dropzone} onClick={() => fileRef.current?.click()}>
            Click to upload an image
          </button>
        )}

        {src ? (
          <div className={styles.controls}>
            <span className={styles.zoomLabel}>Zoom</span>
            <input
              type="range"
              className={styles.zoom}
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              aria-label="Zoom"
            />
          </div>
        ) : null}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onPickFile}
          className={styles.hiddenFile}
        />

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.actions}>
          <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
            {src ? 'Replace image' : 'Upload image'}
          </button>
          {currentIcon ? (
            <button
              type="button"
              className="secondary-function"
              onClick={() => onSave(null)}
              disabled={saving}
            >
              Remove
            </button>
          ) : null}
          <span className={styles.spacer} />
          <button type="button" className="secondary-function" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={onConfirm}
            disabled={!src || !areaPixels || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Draw the selected crop region to a square canvas and export a data URL. */
async function cropToDataUrl(src: string, area: Area, size: number): Promise<string> {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return src;
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
  // Prefer webp; fall back to png where canvas can't encode webp (older Safari).
  const webp = canvas.toDataURL('image/webp', 0.9);
  return webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png');
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
